/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
// ============================================================
// CART SIDEBAR - Enhanced with smooth animations
// ============================================================

import { useEffect } from "react";
import { buildImageUrl } from "../../theme1/utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";

export default function CartSidebar({
  cartOpen,
  setCartOpen,
  cartItems,
  theme,
  updateQtyAPI,
  removeFromCartAPI,

  /* Coupon */
  appliedCoupon,
  couponDiscount,
  couponCode,
  setCouponCode,
  couponError,
  handleApplyCoupon,
  displayCoupons,

  /* Delivery Form */
  deliveryForm,
  setDeliveryForm,
  cities,

  subtotal,
  total,
  shipping,
  handleCheckoutSubmit,
  isSubmitting = false,
  setShowAuthModal
}) {
  // ====== Safe Theme Fallback ======
  const safeTheme = theme || { primary: "#EC4899", accent: "#FCD34D" };

  // ====== Helper Functions ======
  const getQty = (item) => Number(item.quantity || item.qty || 1);
  const getUnitPrice = (item) => Number(item.price || item.unitPrice || 0);

  // Prefill city from last selection (persisted in localStorage)
  useEffect(() => {
    if (deliveryForm?.cityId) return;
    const savedCityId = localStorage.getItem("cartSavedCityId");
    const savedCityName = localStorage.getItem("cartSavedCityName");
    if (savedCityId) {
      setDeliveryForm(prev => ({
        ...prev,
        cityId: savedCityId,
        cityName: savedCityName || prev.cityName || ""
      }));
    }
  }, [deliveryForm?.cityId, setDeliveryForm]);

  return (
    <>
      {/* ====== Backdrop Overlay ====== */}
      <div
        className={`fixed inset-0 bg-black/80 backdrop-blur-md z-[9998] transition-all duration-500 lg:hidden ${cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setCartOpen(false)}
      />

      {/* ====== Cart Sidebar ====== */}
      <aside
        className={`fixed inset-y-0 right-0 w-full max-w-[450px] flex flex-col z-[9999] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{
          background: "linear-gradient(180deg, #0a0a0a 0%, #121212 100%)",
          boxShadow: "-20px 0 50px rgba(0,0,0,0.8)",
          borderLeft: "1px solid rgba(255,255,255,0.05)"
        }}
      >
        {/* ====== Header ====== */}
        <div className="flex justify-between items-center px-6 py-5 border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
          <div className="flex flex-col">
            <h3 className="text-xl font-black text-white flex items-center gap-3">
              <span className="text-2xl">🛍️</span>
              سلة التسوق
            </h3>
            <span className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-bold mt-1">
              {cartItems.reduce((s, i) => s + getQty(i), 0)} منتجات في السلة
            </span>
          </div>
          <button
            onClick={() => setCartOpen(false)}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 text-white hover:bg-red-500/20 hover:text-red-500 transition-all duration-300 group"
          >
            <span className="transform group-hover:rotate-90 transition-transform duration-300 text-lg">✕</span>
          </button>
        </div>

        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-8">

          {/* ====== Coupon Section (Smart & Elegant) ====== */}
          {cartItems.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">🎟️</span>
                <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">كوبونات الخصم</span>
              </div>

              <div className="p-4 rounded-3xl bg-white/5 border border-white/5 space-y-4">
                <form onSubmit={handleApplyCoupon} className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="أدخل كود الخصم"
                      disabled={!!appliedCoupon}
                      className="w-full bg-black border border-white/10 rounded-2xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-all"
                      style={{ backgroundColor: "#000" }}
                    />
                    {appliedCoupon && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 text-xs">✓</span>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={!!appliedCoupon || !couponCode?.trim()}
                    className="px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all active:scale-95 disabled:opacity-30"
                    style={{
                      background: appliedCoupon ? "rgba(255,255,255,0.05)" : `linear-gradient(135deg, ${safeTheme.primary}, #000)`,
                      color: appliedCoupon ? "rgba(255,255,255,0.2)" : "#fff",
                      border: appliedCoupon ? "1px solid rgba(255,255,255,0.05)" : "none"
                    }}
                  >
                    تطبيق
                  </button>
                </form>

                {couponError && <p className="text-[10px] text-red-500 font-bold px-2">⚠️ {couponError}</p>}

                {!appliedCoupon && displayCoupons?.length > 0 && (
                  <div className="pt-2 flex flex-wrap gap-2">
                    {displayCoupons.slice(0, 3).map(c => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => { setCouponCode(c.code); handleApplyCoupon({ preventDefault: () => { } }); }}
                        className="text-[10px] font-bold px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-white/60 hover:text-white hover:border-white/30 transition-all"
                      >
                        {c.code}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ====== Cart Items List ====== */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm">📦</span>
              <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">محتويات السلة</span>
            </div>

            {cartItems.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center text-center space-y-4 bg-white/2 px-6 rounded-[2.5rem] border border-dashed border-white/5">
                <div className="text-5xl opacity-20">🛒</div>
                <div className="space-y-1">
                  <p className="text-white font-bold">السلة فارغة حالياً</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-widest">ابدأ بإضافة بعض المنتجات الرائعة</p>
                </div>
                <button
                  onClick={() => setCartOpen(false)}
                  className="px-6 py-2.5 rounded-full bg-white text-black text-xs font-black mt-4 hover:scale-105 transition-transform"
                >
                  تصفح المنتجات
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {cartItems.map((item) => {
                  const qty = getQty(item);
                  const price = getUnitPrice(item);
                  const totalPrice = qty * price;

                  return (
                    <div
                      key={item.variantId || item.id}
                      className="group relative flex gap-4 p-4 rounded-3xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all duration-500"
                    >
                      {/* Image Container */}
                      <div className="relative w-24 h-24 rounded-2xl overflow-hidden bg-white p-2 flex-shrink-0 group-hover:scale-105 transition-transform duration-500">
                        <img
                          src={buildImageUrl(
                            item.imageUrl ||
                            item.parentImageUrl ||
                            item.mainImage ||
                            item.mainImageUrl ||
                            item.image ||
                            item.img
                          )}
                          alt={item.name || item.itemName}
                          className="w-full h-full object-contain"
                          onError={e => (e.target.src = "https://placehold.co/80x80.png?text=Product")}
                        />
                      </div>

                      {/* Info Container */}
                      <div className="flex-1 flex flex-col justify-between py-1 min-w-0">
                        <div className="space-y-1">
                          <div className="flex justify-between items-start gap-2">
                            <h4 className="text-sm font-bold text-white truncate group-hover:text-white/100 leading-tight">
                              {item.name || item.itemName}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeFromCartAPI(item.variantId || item.id)}
                              className="text-white/20 hover:text-red-500 transition-colors p-1"
                            >
                              ✕
                            </button>
                          </div>

                          {/* Variant Badges */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(item.colorHex || item.color) && (
                              <div className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded-lg border border-white/5">
                                <div className="w-2.5 h-2.5 rounded-full border border-white/20" style={{ background: item.colorHex || item.color }} />
                                <span className="text-[9px] text-white/40 font-bold">اللون</span>
                              </div>
                            )}
                            {(item.size || item.memory) && (
                              <div className="bg-black/30 px-2 py-1 rounded-lg border border-white/5">
                                <span className="text-[9px] text-white/40 font-bold line-clamp-1">المقاس: {item.size || item.memory}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 mt-4">
                          <div className="text-lg font-black" style={{ color: safeTheme.accent }}>
                            {formatNumber(totalPrice)} <span className="text-[10px] font-bold">د.ع</span>
                          </div>

                          {/* Minimalist Quantity Controls */}
                          <div className="flex items-center bg-black/40 rounded-xl border border-white/5 p-1 w-full sm:w-auto self-start sm:self-auto">
                            <button
                              type="button"
                              className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white transition-colors text-sm"
                              onClick={() => qty <= 1 ? removeFromCartAPI(item.variantId || item.id) : updateQtyAPI(item.variantId || item.id, -1)}
                            >−</button>
                            <span className="w-8 text-center text-xs font-black text-white">{qty}</span>
                            <button
                              type="button"
                              disabled={qty >= Number(item.stockQty || 999)}
                              className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white disabled:opacity-10 transition-colors text-sm"
                              onClick={() => updateQtyAPI(item.variantId || item.id, 1)}
                            >+</button>
                          </div>
                        </div>

                        {qty >= Number(item.stockQty || 999) && (
                          <p className="text-[8px] text-red-500 font-bold mt-2 animate-pulse">وصلت للحد الأقصى للمخزون</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ====== Order Summary (Premium Look) ====== */}
          {cartItems.length > 0 && (
            <section className="p-6 rounded-[2.5rem] bg-gradient-to-br from-white/5 to-transparent border border-white/5 space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs text-white/40 font-bold">
                  <span>المجموع الفرعي</span>
                  <span className="text-white">{formatNumber(subtotal)} د.ع</span>
                </div>

                <div className="flex justify-between items-center text-xs text-white/40 font-bold">
                  <span>توصيل الطلب</span>
                  {!deliveryForm.cityId ? (
                    <span className="text-yellow-500 text-[10px]">يرجى اختيار المدينة</span>
                  ) : shipping === 0 ? (
                    <span className="text-green-500 flex items-center gap-1">مجاني ✨</span>
                  ) : (
                    <span className="text-white">{formatNumber(shipping)} د.ع</span>
                  )}
                </div>

                {appliedCoupon && (
                  <div className="flex justify-between items-center text-xs text-green-500 font-bold">
                    <span>الخصم المطبق</span>
                    <span>-{formatNumber(couponDiscount)} د.ع</span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-white/5 flex justify-between items-end">
                <div className="space-y-1">
                  <p className="text-[10px] text-white/40 font-black uppercase tracking-widest">المجموع النهائي</p>
                  <p className="text-3xl font-black text-white leading-none">
                    {formatNumber(total)} <span className="text-xs font-bold text-white/40">د.ع</span>
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* ====== Delivery Form (Modern & Structured) ====== */}
          {cartItems.length > 0 && (
            <section className="space-y-6 pb-20">
              <div className="flex items-center gap-2">
                <span className="text-sm">📍</span>
                <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">معلومات التوصيل</span>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-[10px] text-white/40 font-black uppercase tracking-widest px-2">الاسم الكامل</label>
                  <input
                    placeholder="اكتب اسمك هنا..."
                    value={deliveryForm.name}
                    onChange={e => setDeliveryForm({ ...deliveryForm, name: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl px-4 py-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-all"
                    style={{ backgroundColor: "#000" }}
                  />
                </div>

                {/* Contact Info Row */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] text-white/40 font-black uppercase tracking-widest px-2">رقم الهاتف</label>
                    <input
                      type="tel"
                      placeholder="07xxxxxxxx"
                      value={deliveryForm.phone}
                      onChange={e => setDeliveryForm({ ...deliveryForm, phone: e.target.value.replace(/\D/g, "") })}
                      className="w-full bg-black border border-white/10 rounded-2xl px-4 py-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-all"
                      style={{ backgroundColor: "#000" }}
                    />
                  </div>
                </div>

                {/* City Selection */}
                <div className="space-y-2">
                  <label className="text-[10px] text-white/40 font-black uppercase tracking-widest px-2">المحافظة</label>
                  <div className="relative">
                    <select
                      value={deliveryForm.cityId ? String(deliveryForm.cityId) : ""}
                      onChange={e => {
                        const selectedId = e.target.value;
                        const selectedCity = (cities || []).find(c => String(c.cityId) === String(selectedId));
                        setDeliveryForm(prev => ({
                          ...prev,
                          cityId: selectedId,
                          cityName: selectedCity?.cityName || prev.cityName || ""
                        }));
                        localStorage.setItem("cartSavedCityId", selectedId);
                        localStorage.setItem("cartSavedCityName", selectedCity?.cityName || "");
                      }}
                      className="w-full bg-black border border-white/10 rounded-2xl px-4 py-4 text-sm text-white focus:outline-none focus:border-white/30 transition-all appearance-none cursor-pointer"
                      style={{ backgroundColor: "#000" }}
                    >
                      <option value="" className="bg-[#121212]">اختر المحافظة</option>
                      {(cities || []).map(c => (
                        <option key={c.cityId} value={String(c.cityId)} className="bg-[#121212]">{c.cityName}</option>
                      ))}
                    </select>
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/20 text-xs text-left">▼</div>
                  </div>
                </div>

                {/* Address Tag */}
                <div className="space-y-2">
                  <label className="text-[10px] text-white/40 font-black uppercase tracking-widest px-2">العنوان بالتفصيل</label>
                  <textarea
                    placeholder="مثال: بغداد، حي المنصور، قرب مول..."
                    rows={3}
                    value={deliveryForm.address}
                    onChange={e => setDeliveryForm({ ...deliveryForm, address: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl px-4 py-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-all resize-none"
                    style={{ backgroundColor: "#000" }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] text-white/40 font-black uppercase tracking-widest px-2">الملاحظات</label>
                  <textarea
                    placeholder="أي ملاحظة للتوصيل (اختياري)"
                    rows={2}
                    value={deliveryForm.notes || ""}
                    onChange={e => setDeliveryForm({ ...deliveryForm, notes: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl px-4 py-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-all resize-none"
                    style={{ backgroundColor: "#000" }}
                  />
                </div>
              </div>

              {/* Final Action Button */}
              <button
                type="button"
                disabled={isSubmitting}
                className="w-full py-5 rounded-3xl font-black text-lg transition-all active:scale-[0.98] shadow-2xl relative overflow-hidden group"
                style={{
                  background: `linear-gradient(135deg, ${safeTheme.primary}, #000)`,
                  color: "#fff",
                  boxShadow: `0 10px 40px ${safeTheme.primary}30`,
                  cursor: isSubmitting ? "wait" : "pointer",
                  opacity: isSubmitting ? 0.75 : 1
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCheckoutSubmit(e);
                }}
              >
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 slant-shine pointer-events-none" />
                <span className="relative z-10 flex items-center justify-center gap-3">
                  {isSubmitting ? "جاري إرسال الطلب..." : "تأكيد الطلب الآن 🚀"}
                </span>
              </button>
            </section>
          )}
        </div>

        {/* CSS for custom styling bits */}
        <style jsx="true">{`
          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 10px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }
          .slant-shine {
            transform: skewX(-20deg);
          }
        `}</style>
      </aside>
    </>
  );
}


